import React, { useState } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import classnames from 'classnames';
import { CLONED_CONNECTION, ConnectionVersion, PANELIST_LINK } from '../../../../../constants/common.constants';
import { SingleSelectButton } from '../../../../global/single-select/single-select-button';
import { ConnectionType, IConnectionDetailsDTO, IDeleteConnectionRequestParams } from '../../../../../api/connections.dao';
import { withViewModel } from '../../../../../infrastructure/components/with-view-model';
import { OERadioSingleOption } from '../../../../global/oe-radio-options/oe-radio-single-option';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { CreateZoomWebinarModal } from './_create-zoom-webinar-modal/create-zoom-webinar-modal.presentation';
import { ZoomMeetingModal } from './_zoom-meeting-modal/zoom-meeting-modal.presentation';
import { ConnectionsListVM } from './connections-list.viewmodel';
import { ConnectionDetailsItem } from './_connection-details-item';
import { ConnectionDeleteButton } from './_connection-delete-button';
import { ConfirmDeletePopover, IDeleteConnectionDetails } from './_connection-delete-confirm-popover';
import { MeetingApiParams } from './_connection-form/connection-form.viewmodel';

import './connections-list.scss';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import { KnovioLiveStreamModal } from './knovio-live-stream-modal/knovio-live-stream-modal.presentation';
import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { IOrganizationsForSpeakersSync } from './_connection-form/_sync-speakers/sync-speakers.viewmodel';
import KnovioPreviewLinkPopover from '../knovio-preview-link-popover/knovio-preview-link-popover';
import KnovioPreviewLinkPopoverVM, { KnovioPreviewSourceType } from '../knovio-preview-link-popover/knovio-preview-link-popover.viewmodel';
import { CommonModal } from '../../../../global/modal/common/common-modal';
import KnovioPreviewSpinner from '../../../../global/spinner/knovio-preview-spinner';
import { CreateAudioBridgeModal } from './_audio-bridge-modal/create-audio-bridge-modal.presentation';
import { CreatePanelistLinkModal } from './panelist-link-modal/create-panelist-link-modal.presentation';
import { PANELIST_LINK_CONNECTION_TEXT } from '../../_connections/connections.texts';

export interface IContainerProps {
    isMeetingConnectionsForm?: boolean;
    intl?: IntlShape;
    connections: IConnectionDetailsDTO[];
    connectionTypes: {id: ConnectionType; name: ConnectionType}[];
    startDate: Date;
    endDate: Date;
    timeZone: string;
    topic: string;
    hideDeleteButton?: boolean;
    defaultConnectionId?: number;
    updateConnection: (connection: IConnectionDetailsDTO) => Promise<void>;
    createConnection: (connection: IConnectionDetailsDTO) => Promise<void>;
    meetingApiParams: MeetingApiParams;
    deleteConnection?: (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) => Promise<void>;
    updateDefaultConnection?: (meetingConnectionId: number | null) => void;
    event?: {eventId: number; accountId: number};
    generalMeetingId?: number;
    getKnovioConnectionDetails: (connection: IConnectionDetailsDTO) => Promise<IConnectionDetailsDTO>;
    openFrom?: OpenFrom;
    isKnovioReplayConnectionExist: (meetingConnectionDetails?: IConnectionDetailsDTO) => boolean;
    handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    presenterOrganizationList: IAttendeeOrganizationDTO[];
    meetingTopic?: string;
    organizationsForSpeakersSync?: IOrganizationsForSpeakersSync;
    organization?: string;
    connectionVersion?: ConnectionVersion;
    isMeetingV2?: boolean;
}

const SingleSelectButtonComponent = ({ formVM }: { formVM: ConnectionsListVM }) => {
    const [cloneConnectionStatus, setCloneConnectionStatus] = useState<boolean>(formVM.isCloneConnectionExist());
    const [enablePanelistLink, setEnablePanelistLink] = useState<boolean>(formVM.isWebinarConnectionExist());

    const buttonOption = {
        id: CLONED_CONNECTION,
        name: formVM.getProps().intl.formatMessage({
            id: 'oe.connection.addClonedConnection',
            description: 'Label text',
            defaultMessage: 'Add Cloned Connection'
        }),
        disabled: !cloneConnectionStatus
    };

    const panelistButtonOption = {
        id: PANELIST_LINK,
        name: formVM.getProps().intl.formatMessage(PANELIST_LINK_CONNECTION_TEXT),
        disabled: !enablePanelistLink
    };

    const items = React.useMemo(() => {
        let connectionTypes = formVM.connectionTypesWithOption();
        const isExistOption = Boolean(connectionTypes.find(option => option.id === buttonOption.id));
        const panelistLinkIndex = connectionTypes.findIndex(option => option.id === PANELIST_LINK);
        if (panelistLinkIndex !== -1) {
            connectionTypes[panelistLinkIndex] = panelistButtonOption;
        } else {
            connectionTypes.push(panelistButtonOption);
        }
        if (!isExistOption) {
            connectionTypes.push(buttonOption);
        } else {
            connectionTypes.pop();
            connectionTypes.push(buttonOption);
        }
        return connectionTypes;
    }, [cloneConnectionStatus, enablePanelistLink]);

    const handleStatus = () => {
        const status = formVM.isCloneConnectionExist();
        setCloneConnectionStatus(status);
        setEnablePanelistLink(formVM.isWebinarConnectionExist());
    };

    return hasPermission(Permission.CONNECTIONS_MANAGEMENT) &&
        <SingleSelectButton
            className='default-button add-line-to-table-btn'
            items={items}
            onClick={handleStatus}
            value='+ Add Connection'
            onChange={formVM.onConnectionTypeSelection}
            nameProperty='name'
            keyProperty='id'
            disabled='disabled'
        />;
};


const ConnectionListPresentation = mobxReact.observer((props: {
    vm: ConnectionsListVM;
}) => {
    const isMeetingConnectionsForm = props.vm.isMeetingConnectionsForm;
    const formVM = props.vm;
    const knovioPreviewLinkPopoverInstance = new KnovioPreviewLinkPopoverVM(KnovioPreviewSourceType.Connection);
    const [deletePopoverConnection, setDeletePopoverConnection] = useState<IConnectionDetailsDTO | null>(null);
    const connectionManagementPermission = hasPermission(Permission.CONNECTIONS_MANAGEMENT);
    const shouldRenderKnovioPreFetchLoader = hasPermission(Permission.EDIT_KNOVIO_LIVE_STREAMS);

    const getConnectionSettingsValue = (connection: IConnectionDetailsDTO): string => {
        if (connection.passportReplayEnabled) return 'Replay';
        if (connection.autoRegistrationEnabled) return 'Auto Reg';
        if (connection.autoSyncEnabled) return 'Auto Sync';

        return '';
    };

    const meetingParams: MeetingApiParams = {
        accountId: formVM.getProps()?.event?.accountId,
        eventId: formVM.getProps()?.event?.eventId,
        meetingId: formVM.getProps()?.meetingApiParams?.meetingId,
        roomId: formVM.getProps()?.meetingApiParams?.roomId
    };

    const openKnovioPreviewLinkPopover = connection => {
        if (connection?.type === ConnectionType.Knovio && hasPermission(Permission.PREVIEW_KNOVIO_CONNECTION)) {
            knovioPreviewLinkPopoverInstance.setConnectionInfo({
                ...meetingParams,
                meetingConnectionDetailsId: connection.meetingConnectionId
            });

            knovioPreviewLinkPopoverInstance.openPopover();
        };
    };

    const getSortedConnections = (): IConnectionDetailsDTO[] => {
        return formVM.getProps().connections.reduce((acc, connection) => {
            if (connection.type === ConnectionType.Webinar && connection.childConnections?.length > 0) {
                acc.push(connection);
                connection.childConnections.forEach(childConnection => {
                    childConnection.connectionVersion = ConnectionVersion.V2;
                    childConnection.parentMeetingConnectionId = connection.meetingConnectionId;
                    acc.push(childConnection);
                });
            } else {
                acc.push(connection);
            }
            return acc;
        }, []);
    };

    return (
        <div className={classnames('connection-list', { 'connection-list__meeting-connections': isMeetingConnectionsForm })}>
            <div className={classnames('connection-list__label-wrapper')}>
                <div className={classnames('connection-list__label', { 'connection-list__label--wide': isMeetingConnectionsForm })}>
                    <FormattedMessage id='oe.input.connections' description='Input label' defaultMessage='Connections'/>
                </div>
                <div className={classnames('connection-list__label connection-list__label--greyed', {
                    'connection-list__label--mr': !isMeetingConnectionsForm && !formVM.getProps().hideDeleteButton,
                    })}>
                    <FormattedMessage id='oe.input.default' description='Default label' defaultMessage='Default'/>
                </div>
                {
                    isMeetingConnectionsForm && (
                        <div className={classnames('connection-list__label m-left-10px', {
                            'connection-list__label--mr': !formVM.getProps().hideDeleteButton,
                        })}>
                            <FormattedMessage id='oe.connections.settings' description='Label' defaultMessage='Settings'/>
                        </div>
                    )
                }
            </div>
            {getSortedConnections().map(connection => {
                const shouldDisableDeleteButton = props.vm.shouldDisableDeleteButton(connection);
                return (
                    <div key={connection.joinMethodId} className={classnames(
                        'connection-list__connection-wrapper', {
                            'connection-list__connection-wrapper--pl': isMeetingConnectionsForm
                        }
                    )}>

                        <ConnectionDetailsItem
                            knovioPreCheckLoading={formVM.knovioPreCheckLoading}
                            connection={connection}
                            cloneConnection={formVM.handleCloneConnection}
                            handleOnChange={formVM.onEditConnection}
                            showConfigurePassportForm={(connectionDetails: IConnectionDetailsDTO): void => {
                                formVM.getProps().showConfigurePassportForm(connectionDetails);
                            }}
                            eventTimezone={props.vm.getEventTimeZone()}
                            setDeletePopoverConnection={!formVM.getProps().hideDeleteButton ? setDeletePopoverConnection : undefined}
                            checkbox={false}
                            webinarReportsVM={formVM.getWebinarReportsVM()}
                            handleTroubleshoot={(connectionDetails: IConnectionDetailsDTO) => {
                                formVM.getProps().handleTroubleshoot(connectionDetails, OpenFrom.EditMeetingConnections);
                            }}
                            trackHostJoinClick={(eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
                                formVM.getProps().trackHostJoinClick(eventName, OpenFrom.EditMeetingConnections);
                            }}
                            handleTrackingEvent={(eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CALL) => {
                                formVM.trackEventMixPanel(eventName);
                            }}
                            meetingParams={meetingParams}
                            onOpenKnovioPreviewLinkPopover={() => openKnovioPreviewLinkPopover(connection)}
                            shouldDisableDeleteButton={shouldDisableDeleteButton}
                            connectionVersion={formVM.getProps().connectionVersion}
                            openFrom={formVM.getProps().openFrom}
                        />
                        {connection.meetingConnectionId && <div className='connection-item__radio-default'>
                            <OERadioSingleOption
                                checked={formVM.getProps().defaultConnectionId === connection.meetingConnectionId}
                                value={connection.meetingConnectionId}
                                onToggle={formVM.getProps().updateDefaultConnection}
                                allowUncheck={true}
                            />
                        </div>}
                        {
                            isMeetingConnectionsForm &&
                            <div className='connection-list__settings-value'>
                                {getConnectionSettingsValue(connection)}
                            </div>
                        }
                        { connectionManagementPermission && !formVM.getProps().hideDeleteButton
                            ?
                                <ConnectionDeleteButton
                                    className='organization__remove-label m-right-10px'
                                    setDeletePopoverConnection={setDeletePopoverConnection}
                                    connection={connection}
                                    shouldDisableDeleteButton={shouldDisableDeleteButton}
                                />
                            : <div style={{width: '84px'}}></div>
                        }
                    </div>

            ); })}
            <KnovioPreviewLinkPopover vm={knovioPreviewLinkPopoverInstance}/>
            <ConfirmDeletePopover
                handleOnDelete={formVM.deleteConnectionInternal}
                deletePopoverConnection={deletePopoverConnection}
                setDeletePopoverConnection={setDeletePopoverConnection}
            />
            <SingleSelectButtonComponent formVM={formVM} />
            <ZoomMeetingModal
                vm={formVM.zoomMeetingVM}
                isMeetingConnectionsForm={isMeetingConnectionsForm}
                isAutoSyncLimitReached={formVM.isAutoSyncLimitReached}
            />
            <CreateZoomWebinarModal
                vm={formVM.createZoomWebinarVM}
                isMeetingConnectionsForm={isMeetingConnectionsForm}
                isAutoSyncLimitReached={formVM.isAutoSyncLimitReached}
            />
            <CreateAudioBridgeModal
                vm={formVM.createAudioBridgeVM}
                isMeetingConnectionsForm={isMeetingConnectionsForm}
                isAutoSyncLimitReached={formVM.isAutoSyncLimitReached}
            />
            <CreatePanelistLinkModal vm={formVM.createPanelistLinkVM} />
            <KnovioLiveStreamModal vm={formVM.knovioLiveStreamVM} />
            {shouldRenderKnovioPreFetchLoader && (
                <CommonModal isOpen={formVM.knovioPreCheckLoading} className='knovio-preFetch-loader-popover'>
                    <div className='knovio-preFetch-loader-popover__content-wrapper'>
                        <KnovioPreviewSpinner classname='knovioPreCheckLoading'/>
                        <div className='oe-form-field__info'>
                            <FormattedMessage
                                id='oe.knovio.prefetchLoader'
                                defaultMessage='Loading'
                                description='Info text'
                            />
                        </div>
                    </div>
                </CommonModal>
            )}
        </div>
    );
});



const ConnectionListContainer = withViewModel(
    ConnectionListPresentation,
    (props: IContainerProps) => new ConnectionsListVM({...props}),
    props => [ props.connections, props.startDate, props.endDate, props.timeZone,
        props.topic, props.hideDeleteButton, props.event, props.generalMeetingId,
    ]
);

export { ConnectionListPresentation, ConnectionListContainer as ConnectionsList };
